/**
 * Die Verwaltungsseite — der Bereich der Hausverwaltung.
 *
 * Klar getrennt von der Beiratsansicht: Hier werden Prüfungen angelegt,
 * Unterlagen hochgeladen, Beiräte eingeladen und die Menüpunkte der
 * Unterlagen verwaltet. Der Beirat sieht nichts davon; er bekommt das
 * Ergebnis als seine Prüfung.
 *
 * Der Betreiber (iDWELL) hat denselben Rahmen und zusätzlich den Punkt
 * „Hausverwaltungen", unter dem er Mandanten anlegt — und wählt oben, in
 * welcher Hausverwaltung er gerade arbeitet.
 */

import { useCallback, useEffect, useState } from 'react';

import { api, mandantWaehlen, gewaehlterMandant } from '../../lib/api';
import type { Konto } from '../../lib/typen';
import { Pruefungsliste } from './Pruefungsliste';
import { PruefungVerwalten } from './PruefungVerwalten';
import { Einstellungen } from './Einstellungen';
import { Mandanten } from './Mandanten';
import { Einrichten } from '../Einrichten';
import { LeistenSchalter, VerwaltungSymbol, useLeisteSchmal } from '../../komponenten/Leiste';

export type VerwaltungsBereich = 'pruefungen' | 'neu' | 'pruefung' | 'einstellungen' | 'mandanten';

export interface VerwaltungProps {
  konto: Konto;
  bereich: VerwaltungsBereich;
  pruefungId: string | null;
  onNavigieren: (pfad: string) => void;
  onKontoAktualisiert: (konto: Konto) => void;
  onAbgemeldet: () => void;
}

export function Verwaltung({ konto, bereich, pruefungId, onNavigieren, onKontoAktualisiert, onAbgemeldet }: VerwaltungProps) {
  const [meldung, setMeldung] = useState<string | null>(null);
  const [leisteOffen, setLeisteOffen] = useState(false);
  const [leisteSchmal, setLeisteSchmal] = useLeisteSchmal();
  const istAdmin = konto.benutzer.systemadmin;
  const mandant = konto.mandant;

  useEffect(() => {
    if (!meldung) return;
    const uhr = window.setTimeout(() => setMeldung(null), 4500);
    return () => window.clearTimeout(uhr);
  }, [meldung]);

  const kontoNeuLaden = useCallback(async () => {
    try {
      onKontoAktualisiert(await api.konto.lesen());
    } catch {
      // Beim nächsten Laden wieder.
    }
  }, [onKontoAktualisiert]);

  async function abmelden() {
    try {
      await api.konto.abmelden();
    } finally {
      mandantWaehlen(null);
      onAbgemeldet();
    }
  }

  const punkte: { wert: VerwaltungsBereich; text: string; pfad: string; nurMitMandant?: boolean; nurAdmin?: boolean }[] = [
    { wert: 'pruefungen', text: 'Prüfungen', pfad: '/verwaltung', nurMitMandant: true },
    { wert: 'einstellungen', text: 'Einstellungen', pfad: '/verwaltung/einstellungen', nurMitMandant: true },
    { wert: 'mandanten', text: 'Hausverwaltungen', pfad: '/admin', nurAdmin: true },
  ];
  const aktiv: VerwaltungsBereich = bereich === 'neu' || bereich === 'pruefung' ? 'pruefungen' : bereich;

  return (
    <div className={`shell verwaltung${leisteOffen ? ' leiste-offen' : ''}${leisteSchmal ? ' leiste-schmal' : ''}`}>
      <aside className="seitenleiste">
        <div className="marke">
          <div className="brandmark">iD</div>
          <div className="marke-text">
            <div className="marke-titel">Beiratsprüfung</div>
            <div className="marke-unter">Verwaltung</div>
          </div>
        </div>

        {istAdmin && (
          <div className="mandantwahl">
            <label className="feld" style={{ marginBottom: 0 }}>
              <span>Arbeiten als</span>
              <select
                value={gewaehlterMandant() ?? ''}
                onChange={async (e) => {
                  mandantWaehlen(e.target.value || null);
                  await kontoNeuLaden();
                  onNavigieren('/verwaltung');
                }}
              >
                <option value="">— Hausverwaltung wählen —</option>
                {(konto.mandanten ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        <nav className="seitennav" aria-label="Bereiche">
          {punkte
            .filter((p) => (!p.nurAdmin || istAdmin) && (!p.nurMitMandant || mandant))
            .map((p) => (
              <button
                key={p.wert}
                className="seitennav-punkt"
                aria-current={aktiv === p.wert ? 'page' : undefined}
                title={leisteSchmal ? p.text : undefined}
                onClick={() => {
                  onNavigieren(p.pfad);
                  setLeisteOffen(false);
                }}
              >
                <VerwaltungSymbol wert={p.wert} />
                <span>{p.text}</span>
              </button>
            ))}
        </nav>
        <div className="spacer" />
        <div className="seitenleiste-hilfe">
          <b>{mandant?.name ?? (istAdmin ? 'Betreiber' : '')}</b>
          <p>
            {mandant
              ? mandant.idwellVerbunden
                ? 'Mit iDWELL verbunden.'
                : 'Kein iDWELL-Schlüssel — Belege kommen aus dem Importpaket.'
              : istAdmin
                ? 'Wählen Sie oben eine Hausverwaltung oder legen Sie eine an.'
                : ''}
          </p>
        </div>
        <LeistenSchalter schmal={leisteSchmal} onWechsel={setLeisteSchmal} />
      </aside>

      <div className="hauptbereich">
        <header className="kopfzeile">
          <button className="iconbtn nur-schmal" aria-label="Menü" onClick={() => setLeisteOffen((o) => !o)}>
            ☰
          </button>
          <div className="brotkrumen">
            <span className="brotkrumen-weg">Verwaltung</span>
            {mandant && (
              <>
                <span className="brotkrumen-trenner">/</span>
                <span className="brotkrumen-ort">{mandant.name}</span>
              </>
            )}
          </div>
          <div className="spacer" />
          {mandant && bereich !== 'neu' && (
            <button className="btn primary sm" onClick={() => onNavigieren('/verwaltung/neu')}>
              Neue Prüfung
            </button>
          )}
          <div className="nutzer" title={konto.benutzer.email}>
            <span className="nutzer-kreis">{konto.benutzer.name.slice(0, 2).toUpperCase()}</span>
            <span className="nutzer-text">
              <b>{konto.benutzer.name}</b>
              <small>{istAdmin ? 'Betreiber' : 'Hausverwaltung'}</small>
            </span>
          </div>
          <button className="btn ghost sm" onClick={abmelden}>
            Abmelden
          </button>
        </header>

        <main className="inhalt">
          <section className="seite">
            {bereich === 'mandanten' && istAdmin && <Mandanten onMeldung={setMeldung} onGeaendert={kontoNeuLaden} />}

            {!mandant && bereich !== 'mandanten' && (
              <div className="wrap">
                <div className="leer card">
                  <h3>{istAdmin ? 'Keine Hausverwaltung gewählt' : 'Kein Mandant'}</h3>
                  <p>
                    {istAdmin
                      ? 'Wählen Sie links eine Hausverwaltung, in der Sie arbeiten möchten, oder legen Sie unter „Hausverwaltungen" eine an.'
                      : 'Ihr Zugang ist keiner Hausverwaltung zugeordnet. Bitte wenden Sie sich an den Betreiber.'}
                  </p>
                </div>
              </div>
            )}

            {mandant && bereich === 'pruefungen' && (
              <Pruefungsliste onOeffnen={(id) => onNavigieren(`/verwaltung/pruefung/${id}`)} onNeu={() => onNavigieren('/verwaltung/neu')} onMeldung={setMeldung} />
            )}

            {mandant && bereich === 'neu' && (
              <div className="wrap">
                <Einrichten idwellVerbunden={mandant.idwellVerbunden} onFertig={(id) => onNavigieren(`/verwaltung/pruefung/${id}`)} />
              </div>
            )}

            {mandant && bereich === 'pruefung' && pruefungId && (
              <PruefungVerwalten
                pruefungId={pruefungId}
                mandant={mandant}
                onZurueck={() => onNavigieren('/verwaltung')}
                onOeffnen={() => onNavigieren(`/pruefung/${pruefungId}`)}
                onMeldung={setMeldung}
              />
            )}

            {mandant && bereich === 'einstellungen' && (
              <Einstellungen konto={konto} onMeldung={setMeldung} onGeaendert={kontoNeuLaden} />
            )}
          </section>
        </main>
      </div>

      {meldung && <div className="toast">{meldung}</div>}
    </div>
  );
}
